import { FileCheck2Icon, LandmarkIcon, LockKeyholeIcon, TimerIcon } from 'lucide-react'
import type { DisclosureContent, ProductPageContent } from '../../types/product'
import { industryCatalog, productCatalog } from './shared'

export const financeDisclosure: DisclosureContent = {
  title: 'Important information about financing',
  items: [
    { label: 'Lender', value: 'Financing is provided by a licensed lending partner, not by Kayko.' },
    { label: 'Licence', value: 'Licensed and supervised by the National Bank of Rwanda (BNR).' },
    {
      label: 'Representative example',
      value: 'Borrow RWF 2,000,000 over 6 months at a monthly fee of 2.4%. Total repayable RWF 2,288,000.',
    },
    {
      label: 'Eligibility',
      value: 'Registered Rwandan businesses with at least 6 months of sales recorded in Kayko. Subject to assessment.',
    },
  ],
  notes: [
    'Financing is subject to eligibility, assessment, and the lender’s terms. Late or missed repayments may affect your ability to access credit in future.',
  ],
}

export const businessFinance: ProductPageContent = {
  slug: 'business-finance',
  name: 'Business Financing',
  icon: LandmarkIcon,
  hero: {
    headline: 'Turn your business records into working capital',
    subheadline:
      'Your sales and expense history in Kayko shows how your business performs. Use it to check your eligibility for financing, without piles of paperwork.',
    trustLine: 'Through licensed lending partners · RWF 1.8B+ unlocked for Kayko businesses',
    visual: {
      kind: 'devices',
      tablet: {
        module: 'Finance',
        greeting: 'Business Financing · Inzozi Mini Market',
        kpis: [
          { label: 'Records complete', value: '82%', delta: { label: '2 steps left', tone: 'brand' } },
          { label: 'Avg. monthly sales', value: 'RWF 9.6M' },
          { label: 'Months recorded', value: '14' },
        ],
        main: {
          type: 'ring',
          title: 'Eligibility check',
          meta: 'Based on your Kayko records',
          pct: 82,
          pctLabel: 'profile complete',
          checks: ['14 months of sales history', 'Expenses recorded monthly', 'Business registration added'],
        },
        side: {
          type: 'list',
          title: 'Estimated range',
          rows: [
            { label: 'Up to', value: 'RWF 3,500,000', tone: 'brand' },
            { label: 'Term', value: '3–9 months' },
            { label: 'Decision', value: '48 hours' },
          ],
        },
      },
      phone: {
        type: 'list',
        title: 'Offer ready',
        badge: { label: 'New', tone: 'positive' },
        rows: [
          { label: 'Amount', value: 'RWF 2,000,000' },
          { label: 'Term', value: '6 months' },
          { label: 'Monthly fee', value: '2.4%' },
        ],
        action: 'Review offer',
      },
    },
  },
  stats: [
    { value: 'RWF 1.8B+', label: 'in working capital accessed by Kayko businesses' },
    { value: '48 hrs', label: 'typical time to a decision from our lending partners' },
    { value: '0', label: 'paper documents needed to check eligibility' },
  ],
  benefits: {
    heading: 'Financing built on how you really trade.',
    items: [
      { icon: FileCheck2Icon, title: 'Records do the work', copy: 'Your sales and expenses in Kayko replace most of the paperwork lenders usually ask for.' },
      { icon: TimerIcon, title: 'Fast decisions', copy: 'Check eligibility in minutes and hear back from lending partners within days.' },
      { icon: LockKeyholeIcon, title: 'You stay in control', copy: 'Your data is only shared with a lender when you choose to apply.' },
    ],
  },
  features: {
    heading: 'From daily records to working capital.',
    subheading: 'Designed for growing businesses that need stock, equipment, or breathing room.',
    items: [
      {
        title: 'Build a verified business record',
        copy: 'Every sale, expense, and payment you record builds a clear picture of your business. The more complete your records, the stronger your profile.',
        bullets: ['Sales history verified through EBM receipts', 'Monthly revenue and expense trends', 'Profile checklist to strengthen your record'],
        visual: {
          kind: 'card',
          card: {
            type: 'ring',
            title: 'Business record',
            meta: 'Inzozi Mini Market · Remera',
            pct: 82,
            pctLabel: 'profile complete',
            checks: ['14 months of sales history', 'Expenses recorded monthly', 'Supplier payments on time'],
            action: 'Complete your profile',
          },
        },
      },
      {
        title: 'Check your eligibility in minutes',
        copy: 'See an estimated financing range based on your records, before you apply. Checking doesn’t affect your credit history.',
        bullets: ['Estimated amount and terms upfront', 'No impact on your credit history', 'Clear reasons if you’re not eligible yet'],
        visual: {
          kind: 'card',
          card: {
            type: 'list',
            title: 'Eligibility result',
            badge: { label: 'Eligible', tone: 'positive' },
            rows: [
              { label: 'Estimated range', value: 'RWF 1M – 3.5M' },
              { label: 'Term', value: '3 – 9 months' },
              { label: 'Monthly fee from', value: '2.2%' },
              { label: 'Based on', value: '14 months of records' },
            ],
            action: 'See offers',
          },
        },
      },
      {
        title: 'Compare clear offers',
        copy: 'Review offers from licensed partners with every fee shown upfront. Know exactly what you’ll repay before you accept.',
        bullets: ['Total cost shown in RWF', 'Repayment schedule before you sign', 'No hidden fees'],
        visual: {
          kind: 'card',
          card: {
            type: 'list',
            title: 'Offer · Working capital',
            meta: 'From a licensed lending partner',
            rows: [
              { label: 'Amount', value: 'RWF 2,000,000' },
              { label: 'Term', value: '6 months' },
              { label: 'Monthly fee', value: '2.4%' },
              { label: 'Monthly repayment', value: 'RWF 381,333' },
            ],
            footer: { label: 'Total repayable', value: 'RWF 2,288,000' },
            action: 'Accept offer',
          },
        },
      },
      {
        title: 'Repayments alongside daily operations',
        copy: 'Track repayments in the same place you run your business. See what’s paid, what’s next, and how financing affects your cash flow.',
        bullets: ['Repayment schedule and reminders', 'Pay by mobile money or bank transfer', 'Cash flow view that includes repayments'],
        visual: {
          kind: 'card',
          card: {
            type: 'progress',
            title: 'Repayments',
            meta: 'RWF 1,144,000 of RWF 2,288,000',
            badge: { label: 'On track', tone: 'positive' },
            rows: [
              { label: 'July', value: 'Paid', pct: 100, tone: 'positive' },
              { label: 'August', value: 'Paid', pct: 100, tone: 'positive' },
              { label: 'September', value: 'Paid', pct: 100, tone: 'positive' },
              { label: 'October', value: 'Due 15 Oct', pct: 0 },
            ],
          },
        },
      },
    ],
  },
  steps: {
    heading: 'Three steps to working capital.',
    items: [
      { title: 'Keep your records in Kayko', copy: 'Record sales and expenses as you trade for at least six months.' },
      { title: 'Check your eligibility', copy: 'See an estimated range in minutes, with no impact on your credit history.' },
      { title: 'Choose an offer', copy: 'Review terms from licensed partners and accept the one that fits.' },
    ],
  },
  related: {
    heading: 'Works with the rest of Kayko.',
    subheading: 'The more you run in Kayko, the stronger your business record.',
    items: [productCatalog['sales-pos'], productCatalog.expenses, productCatalog.reports, productCatalog.inventory],
  },
  industries: {
    heading: 'Built for your industry.',
    subheading: 'Working capital for stock, equipment, renovations, and growth.',
    items: [industryCatalog.retail, industryCatalog.food, industryCatalog.manufacturing, industryCatalog.hotels],
  },
  testimonial: {
    quote:
      'The bank always asked for documents I didn’t have. With my Kayko records I checked my eligibility in minutes and had the money for my new stock before the festive season.',
    name: 'Solange Ingabire',
    role: 'Owner',
    business: 'Ingabire Fashion House',
    location: 'Kimironko, Kigali',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/55a948a8-f1a2-4b85-9533-52b1fa01fe2f.jpg',
    metric: { value: 'RWF 3M', label: 'working capital accessed for festive-season stock' },
  },
  pricingTeaser: {
    text: 'Checking your eligibility is free for every Kayko customer.',
    linkLabel: 'See pricing',
    href: '#',
  },
  faq: {
    heading: 'Frequently asked questions',
    items: [
      { question: 'Who provides the financing?', answer: 'Financing comes from licensed lending partners supervised by the National Bank of Rwanda. Kayko helps you check eligibility and share your records.' },
      { question: 'Who is eligible?', answer: 'Registered Rwandan businesses with at least six months of sales recorded in Kayko. Each lender makes its own assessment.' },
      { question: 'Does checking eligibility affect my credit?', answer: 'No. Checking your estimated range uses your Kayko records only. A lender may run further checks only if you choose to apply.' },
      { question: 'What does it cost?', answer: 'Checking is free. If you accept an offer, the lender’s fees are shown in RWF before you sign, including the total amount to repay.' },
      { question: 'What data is shared with lenders?', answer: 'Only the records you agree to share when you apply, such as sales and expense summaries. You can see exactly what is shared.' },
      { question: 'What if I’m not eligible yet?', answer: 'Kayko shows what would strengthen your profile — for example, more months of records — so you know what to work on.' },
    ],
  },
  disclosure: financeDisclosure,
  cta: {
    headline: 'See what your records can unlock.',
    subheadline: 'Check your eligibility in minutes, with support from our team in Kigali.',
  },
}
